import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { TranscriptionContentResponse, TranscriptionResponse } from 'api-transcription';

@Component({
  selector: 'app-video-timeline',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatSliderModule,
    MatTooltipModule
  ],
  templateUrl: './video-timeline.component.html',
  styleUrl: './video-timeline.component.scss'
})
export class VideoTimelineComponent implements OnInit, OnChanges {
  @Input() transcriptionContent: TranscriptionContentResponse[] = [];
  @Input() totalDuration: number = 0;
  @Input() transcription: TranscriptionResponse | null = null;
  @Input() externalCurrentTime: number = 0;
  @Output() timeChanged = new EventEmitter<number>();
  @Output() transcriptionSegmentClicked = new EventEmitter<number>();
  @Output() durationChanged = new EventEmitter<number>();
  @Output() videoVisibilityToggled = new EventEmitter<boolean>();

  @Input() isVideoFile: boolean = false;
  @Input() isVideoVisible: boolean = true;
  @Input() captionsEnabled: boolean = false;
  @Output() captionsToggled = new EventEmitter<boolean>();
  @Input() mergedTextEnabled: boolean = false;
  @Output() mergedTextToggled = new EventEmitter<boolean>();

  @ViewChild('timeline', { static: true }) timelineRef!: ElementRef<HTMLDivElement>;
  @ViewChild('audioPlayer', { static: true }) audioPlayerRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('videoPlayer') videoPlayerRef!: ElementRef<HTMLVideoElement>;

  currentTime: number = 0;
  isPlaying: boolean = false;
  audioUrl: string = '';
  audioLoaded: boolean = false;
  volume: number = 100;
  private previousVolume: number = 100;
  private isSeekingAudio: boolean = false;
  private currentlyHighlightedSegment: number = -1;
  currentCaption: TranscriptionContentResponse | null = null;

  timelineSegments: Array<{
    content: TranscriptionContentResponse;
    startPercent: number;
    widthPercent: number;
    color: string;
  }> = [];

  speakerColors = [
    '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
    '#d32f2f', '#00796b', '#5d4037', '#303f9f'
  ];

  private speakersMap: Map<string, number> = new Map();

  ngOnInit() {
    this.updateTimelineSegments();
    document.documentElement.style.setProperty('--slider-progress', `${this.volume}%`);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['transcriptionContent'] || changes['totalDuration']) {
      this.updateTimelineSegments();
      this.currentlyHighlightedSegment = -1;
    }
    if (changes['transcription']) {
      this.updateAudioUrl();
    }
    if (changes['externalCurrentTime'] && !this.isPlaying) {
      this.setCurrentTime(this.externalCurrentTime);
    }
    if (changes['captionsEnabled']) {
      if (!this.captionsEnabled) {
        this.currentCaption = null;
      } else {
        this.updateCurrentCaption();
      }
    }
  }

  private updateTimelineSegments() {
    this.buildSpeakersMap();
    this.timelineSegments = [];

    if (!this.transcriptionContent || this.totalDuration === 0) {
      return;
    }

    this.transcriptionContent.forEach(content => {
      const startTime = content.startTime || 0;
      const endTime = content.endTime || startTime + 1000;

      const startPercent = (startTime / this.totalDuration) * 100;
      const widthPercent = ((endTime - startTime) / this.totalDuration) * 100;

      const speakerColorIndex = this.getSpeakerColor(content.speaker || 'Unknown');
      const color = this.speakerColors[speakerColorIndex];

      this.timelineSegments.push({
        content,
        startPercent,
        widthPercent,
        color
      });
    });
  }

  private buildSpeakersMap() {
    this.speakersMap.clear();
    if (this.transcriptionContent) {
      let colorIndex = 0;
      this.transcriptionContent.forEach(item => {
        const speaker = item.speaker || 'Unknown';
        if (!this.speakersMap.has(speaker)) {
          this.speakersMap.set(speaker, colorIndex % this.speakerColors.length);
          colorIndex++;
        }
      });
    }
  }

  private getSpeakerColor(speaker: string): number {
    if (!this.speakersMap.has(speaker)) {
      const colorIndex = this.speakersMap.size % this.speakerColors.length;
      this.speakersMap.set(speaker, colorIndex);
      return colorIndex;
    }
    return this.speakersMap.get(speaker) || 0;
  }

  formatTime(milliseconds: number): string {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  getSegmentTooltip(segment: any): string {
    const startTime = this.formatTime(segment.content.startTime || 0);
    const endTime = this.formatTime(segment.content.endTime || 0);
    const speaker = segment.content.speaker || 'Unknown Speaker';
    const text = segment.content.text || '';
    const truncatedText = text.length > 50 ? text.slice(0, 50) + '...' : text;

    return `[${startTime} - ${endTime}] > ${speaker}: ${truncatedText}`;
  }

  getCurrentTimePercent(): number {
    if (this.totalDuration === 0) return 0;
    return (this.currentTime / this.totalDuration) * 100;
  }

  onTimelineClick(event: MouseEvent) {
    const rect = this.timelineRef.nativeElement.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percent = (clickX / rect.width) * 100;
    const newTime = (percent / 100) * this.totalDuration;

    this.setCurrentTime(newTime);
  }

  onSegmentClick(segment: any, event: MouseEvent) {
    event.stopPropagation();
    const contentIndex = this.transcriptionContent.findIndex(c =>
      c.id ? c.id === segment.content.id : c === segment.content
    );
    if (contentIndex >= 0) {
      this.transcriptionSegmentClicked.emit(contentIndex);
      this.setCurrentTime(segment.content.startTime || 0, contentIndex);
    }
  }


  toggleVideoVisibility() {
    this.videoVisibilityToggled.emit(!this.isVideoVisible);
  }

  toggleCaptions() {
    this.captionsToggled.emit(!this.captionsEnabled);
  }

  toggleMergedText() {
    this.mergedTextToggled.emit(!this.mergedTextEnabled);
  }

  jumpToTime(time: number) {
    this.setCurrentTime(time);
  }

  onVideoLoaded() {
    if (this.videoPlayerRef?.nativeElement) {
      const video = this.videoPlayerRef.nativeElement;
      video.volume = this.volume / 100;
      if (video.duration && video.duration > 0) {
        const videoDurationMs = video.duration * 1000;
        this.durationChanged.emit(videoDurationMs);
      }
    }
  }

  onVideoTimeUpdate() {
    if (this.videoPlayerRef?.nativeElement) {
      const video = this.videoPlayerRef.nativeElement;
      if (video.currentTime !== undefined) {
        const newTime = video.currentTime * 1000;
        this.currentTime = newTime;
        this.timeChanged.emit(this.currentTime);
        this.updateCurrentCaption();
        this.highlightCurrentSegment();
      }
    }
  }

  onVideoError(event: Event) {
    console.error('Video loading error:', event);
  }

  private updateCurrentCaption() {
    if (!this.captionsEnabled || !this.transcriptionContent) {
      this.currentCaption = null;
      return;
    }

    const currentTime = this.currentTime;
    const activeContent = this.transcriptionContent.find(content => {
      const startTime = content.startTime || 0;
      const endTime = content.endTime || startTime + 1000;
      return currentTime >= startTime && currentTime <= endTime;
    });

    this.currentCaption = activeContent || null;
  }

  getCaptionStyles(caption: TranscriptionContentResponse): any {
    const speakerColors = [
      '#1976d2', '#388e3c', '#f57c00', '#7b1fa2',
      '#d32f2f', '#00796b', '#5d4037', '#303f9f'
    ];

    const speakerColorIndex = this.getSpeakerColor(caption.speaker || 'Unknown');
    const color = speakerColors[speakerColorIndex];

    return {
      'border-left': `4px solid ${color}`,
      'color': '#ffffff',
      'text-shadow': '2px 2px 4px rgba(0, 0, 0, 0.8)'
    };
  }

  togglePlayPause() {
    if (this.videoPlayerRef?.nativeElement && this.isVideoFile) {
      const video = this.videoPlayerRef.nativeElement;
      if (this.isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      this.isPlaying = !this.isPlaying;
    }
    else if (this.audioLoaded) {
      const audio = this.audioPlayerRef.nativeElement;
      if (this.isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      this.isPlaying = !this.isPlaying;
    }
  }

  goToPreviousSegment() {
    if (!this.transcriptionContent || this.transcriptionContent.length === 0) return;

    const currentSegmentIndex = this.getCurrentSegmentIndex();
    const previousIndex = Math.max(0, currentSegmentIndex - 1);

    if (previousIndex >= 0 && this.transcriptionContent[previousIndex]) {
      const startTime = this.transcriptionContent[previousIndex].startTime || 0;
      this.setCurrentTime(startTime);
      this.transcriptionSegmentClicked.emit(previousIndex);
    }
  }

  goToNextSegment() {
    if (!this.transcriptionContent || this.transcriptionContent.length === 0) return;

    const currentSegmentIndex = this.getCurrentSegmentIndex();
    const nextIndex = Math.min(this.transcriptionContent.length - 1, currentSegmentIndex + 1);

    if (nextIndex < this.transcriptionContent.length && this.transcriptionContent[nextIndex]) {
      const startTime = this.transcriptionContent[nextIndex].startTime || 0;
      this.setCurrentTime(startTime);
      this.transcriptionSegmentClicked.emit(nextIndex);
    }
  }

  private getCurrentSegmentIndex(): number {
    if (!this.transcriptionContent || this.transcriptionContent.length === 0) return 0;

    const matchingSegments: number[] = [];

    for (let i = 0; i < this.transcriptionContent.length; i++) {
      const segment = this.transcriptionContent[i];
      const startTime = segment.startTime || 0;
      const endTime = segment.endTime || startTime + 1000;

      if (this.currentTime >= startTime && this.currentTime <= endTime) {
        matchingSegments.push(i);
      }
    }

    if (matchingSegments.length > 1) {
      let bestIndex = matchingSegments[0];
      let smallestDistanceFromStart = Math.abs(this.currentTime - (this.transcriptionContent[bestIndex].startTime || 0));

      for (let i = 1; i < matchingSegments.length; i++) {
        const segmentIndex = matchingSegments[i];
        const distanceFromStart = Math.abs(this.currentTime - (this.transcriptionContent[segmentIndex].startTime || 0));

        if (distanceFromStart < smallestDistanceFromStart) {
          smallestDistanceFromStart = distanceFromStart;
          bestIndex = segmentIndex;
        }
      }

      return bestIndex;
    }

    if (matchingSegments.length === 1) {
      return matchingSegments[0];
    }

    let closestIndex = 0;
    let minDistance = Math.abs(this.currentTime - (this.transcriptionContent[0].startTime || 0));

    for (let i = 1; i < this.transcriptionContent.length; i++) {
      const distance = Math.abs(this.currentTime - (this.transcriptionContent[i].startTime || 0));
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  }

  goToStart() {
    this.setCurrentTime(0);
  }

  goToEnd() {
    this.setCurrentTime(this.totalDuration);
  }

  private updateAudioUrl() {
    if (this.transcription?.fileUrl && !this.isVideoFile) {
      this.audioUrl = this.transcription.fileUrl;
      this.audioLoaded = false;
    } else {
      this.audioUrl = '';
      this.audioLoaded = false;
    }
  }

  onAudioLoaded() {
    this.audioLoaded = true;
    const audio = this.audioPlayerRef.nativeElement;
    audio.volume = this.volume / 100;

    if (audio.duration && audio.duration > 0) {
      const audioDurationMs = audio.duration * 1000;
      this.durationChanged.emit(audioDurationMs);
    }
  }

  onAudioTimeUpdate() {
    if (this.isSeekingAudio) return;

    const audio = this.audioPlayerRef.nativeElement;
    if (audio.currentTime !== undefined) {
      const newTime = audio.currentTime * 1000;
      this.currentTime = newTime;
      this.timeChanged.emit(this.currentTime);

      this.highlightCurrentSegment();
    }
  }

  private highlightCurrentSegment() {
    if (!this.transcriptionContent || this.transcriptionContent.length === 0) return;

    const activeSegmentIndex = this.getCurrentSegmentIndex();

    if (activeSegmentIndex !== this.currentlyHighlightedSegment) {
      this.currentlyHighlightedSegment = activeSegmentIndex;

      if (activeSegmentIndex >= 0) {
        this.transcriptionSegmentClicked.emit(activeSegmentIndex);
      }
    }
  }

  onAudioEnded() {
    this.isPlaying = false;
  }

  onAudioError(event: Event) {
    console.error('Audio loading error:', event);
    this.audioLoaded = false;
  }

  setCurrentTime(time: number, targetSegmentIndex?: number) {
    this.currentTime = Math.max(0, Math.min(time, this.totalDuration));

    if (targetSegmentIndex !== undefined) {
      this.currentlyHighlightedSegment = targetSegmentIndex;
    } else {
      this.currentlyHighlightedSegment = -1;
    }

    if (this.videoPlayerRef?.nativeElement && this.isVideoFile) {
      const video = this.videoPlayerRef.nativeElement;
      video.currentTime = this.currentTime / 1000;
    }
    else if (this.audioLoaded) {
      this.isSeekingAudio = true;
      const audio = this.audioPlayerRef.nativeElement;
      audio.currentTime = this.currentTime / 1000;

      setTimeout(() => {
        this.isSeekingAudio = false;
        if (targetSegmentIndex === undefined) {
          this.highlightCurrentSegment();
        }
      }, 100);
    } else {
      this.timeChanged.emit(this.currentTime);
      if (targetSegmentIndex === undefined) {
        this.highlightCurrentSegment();
      }
      this.updateCurrentCaption();
    }
  }

  onVolumeChange(event: any) {
    const value = Number(event.target.value);
    this.volume = value;

    if (value > 0) {
      this.previousVolume = value;
    }

    const volumeLevel = value / 100;

    document.documentElement.style.setProperty('--slider-progress', `${value}%`);

    if (this.videoPlayerRef?.nativeElement && this.isVideoFile) {
      this.videoPlayerRef.nativeElement.volume = volumeLevel;
    }

    if (this.audioPlayerRef?.nativeElement) {
      this.audioPlayerRef.nativeElement.volume = volumeLevel;
    }
  }

  toggleMute() {
    if (this.volume === 0) {
      this.volume = this.previousVolume;
    } else {
      this.previousVolume = this.volume;
      this.volume = 0;
    }

    const volumeLevel = this.volume / 100;

    document.documentElement.style.setProperty('--slider-progress', `${this.volume}%`);

    if (this.videoPlayerRef?.nativeElement && this.isVideoFile) {
      this.videoPlayerRef.nativeElement.volume = volumeLevel;
    }

    if (this.audioPlayerRef?.nativeElement) {
      this.audioPlayerRef.nativeElement.volume = volumeLevel;
    }
  }

  getVolumeIcon(): string {
    if (this.volume === 0) {
      return 'volume_off';
    } else if (this.volume < 30) {
      return 'volume_mute';
    } else if (this.volume < 70) {
      return 'volume_down';
    } else {
      return 'volume_up';
    }
  }
}